/**
 * Lithuanian Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const lt: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Valdymo skydelis',
  'nav.funds': 'Fondai',
  'nav.investments': 'Investicijos',
  'nav.investees': 'Investicijų gavėjai',
  'nav.esg': 'ESG Apžvalga',
  'nav.compliance': 'Atitiktis',
  'nav.reports': 'Ataskaitos',
  'nav.settings': 'Nustatymai',
  'nav.users': 'Naudotojai',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Rodikliai',
  'nav.taxonomy': 'ES Taksonomija',
  'nav.filings': 'Pateikimai',
  'nav.calendar': 'Kalendorius',
  'nav.submit': 'Pateikti duomenis',
  'nav.submissions': 'Mano pateikimai',
  'nav.benchmarks': 'Lyginamieji rodikliai',
  'nav.messages': 'Žinutės',
  'nav.portfolio': 'Portfelis',
  'nav.performance': 'Fondų rezultatai',
  'nav.esgSummary': 'ESG Santrauka',
  'nav.pipeline': 'Konvejeris',
  'nav.dueDiligence': 'Išsamus patikrinimas',
  'nav.impact': 'Poveikio rodikliai',
  'nav.sdg': 'DVT Žemėlapis',
  'nav.audit': 'Audito pėdsakas',
  'nav.snapshots': 'Duomenų momentinės kopijos',
  'nav.knowledgeGraph': 'Žinių grafas',
  'nav.geographic': 'Geografinis',
  'nav.bi': 'BI Studija',
  'nav.ai': 'AI Asistentas',
  'nav.tasks': 'Užduotys',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Reguliacinis monitorius',

  // Common Actions
  'action.create': 'Sukurti',
  'action.edit': 'Redaguoti',
  'action.delete': 'Ištrinti',
  'action.save': 'Išsaugoti',
  'action.cancel': 'Atšaukti',
  'action.submit': 'Pateikti',
  'action.approve': 'Patvirtinti',
  'action.reject': 'Atmesti',
  'action.download': 'Atsisiųsti',
  'action.export': 'Eksportuoti',
  'action.import': 'Importuoti',
  'action.filter': 'Filtruoti',
  'action.search': 'Ieškoti',
  'action.refresh': 'Atnaujinti',
  'action.close': 'Uždaryti',
  'action.back': 'Atgal',
  'action.next': 'Kitas',
  'action.previous': 'Ankstesnis',
  'action.viewAll': 'Peržiūrėti viską',
  'action.viewDetails': 'Peržiūrėti detales',
  'action.generate': 'Generuoti',
  'action.signOut': 'Atsijungti',
  'action.addNew': 'Pridėti naują',
  'action.invite': 'Pakviesti',

  // Statuses
  'status.draft': 'Juodraštis',
  'status.submitted': 'Pateikta',
  'status.validated': 'Patvirtinta',
  'status.approved': 'Patvirtinta',
  'status.rejected': 'Atmesta',
  'status.pending': 'Laukiama',
  'status.inProgress': 'Vykdoma',
  'status.overdue': 'Vėluojama',
  'status.compliant': 'Atitinkama',
  'status.filed': 'Pateikta',
  'status.active': 'Aktyvu',
  'status.inactive': 'Neaktyvu',
  'status.closed': 'Uždaryta',
  'status.fundraising': 'Lėšų rinkimas',
  'status.investing': 'Investavimas',
  'status.harvesting': 'Derliaus nuėmimas',
  'status.validating': 'Tikrinimas',
  'status.review': 'Peržiūroje',
  'status.monitoring': 'Stebėjimas',
  'status.exiting': 'Išėjimas',
  'status.pipeline': 'Pipeline',
  'status.due_diligence': 'Deramas patikrinimas',
  'status.disbursed': 'Išmokėta',
  'status.restructured': 'Restruktūrizuota',
  'status.matured': 'Suėjęs',
  'status.written_off': 'Nurašyta',
  'status.exited': 'Išėjęs',
  'status.incomplete': 'Nebaigta',
  'status.open': 'Atvira',
  'status.in_progress': 'Vykdoma',
  'status.completed': 'Baigta',
  'status.cancelled': 'Atšaukta',
  'status.accepted': 'Priimta',
  'status.failed': 'Nepavyko',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Portfelio apžvalga',
  'dashboard.complianceStatus': 'Atitikties būsena',
  'dashboard.collectionProgress': 'Rinkimo progresas',
  'dashboard.activityFeed': 'Veikla',
  'dashboard.quickActions': 'Greiti veiksmai',
  'dashboard.investmentPipeline': 'Investicijų konvejeris',
  'dashboard.totalFunds': 'Iš viso fondų',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Aktyvios investicijos',
  'dashboard.totalInvestees': 'Investicijų gavėjai',
  'dashboard.recentActivity': 'Naujausia veikla',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Iš viso įsipareigota',
  'lp.capitalCalled': 'Pareikalauta kapitalo',
  'lp.distributed': 'Paskirstyta',
  'lp.netPosition': 'Grynoji pozicija',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Fondų rezultatai',
  'lp.esgSummary': 'ESG Santrauka',
  'lp.reportLibrary': 'Ataskaitų biblioteka',
  'lp.taxonomyAligned': 'Suderinta su taksonomija',
  'lp.paiCoverage': 'PAI Aprėptis',
  'lp.portfolioPerformance': 'LP Portfelio rezultatai',

  // Fund Labels
  'fund.name': 'Fondo pavadinimas',
  'fund.code': 'Fondo kodas',
  'fund.vintage': 'Įsteigimo metai',
  'fund.size': 'Fondo dydis',
  'fund.targetSize': 'Tikslinis dydis',
  'fund.currency': 'Valiuta',
  'fund.strategy': 'Strategija',
  'fund.status': 'Būsena',
  'fund.sfdrCategory': 'SFDR Kategorija',
  'fund.geographyFocus': 'Geografinis fokusas',
  'fund.sectorFocus': 'Sektoriaus fokusas',

  // Form Labels
  'form.name': 'Pavadinimas',
  'form.email': 'El. paštas',
  'form.country': 'Šalis',
  'form.amount': 'Suma',
  'form.currency': 'Valiuta',
  'form.date': 'Data',
  'form.description': 'Aprašymas',
  'form.notes': 'Pastabos',
  'form.type': 'Tipas',
  'form.period': 'Laikotarpis',
  'form.year': 'Metai',
  'form.required': 'Privalomas',
  'form.optional': 'Neprivalomas',

  // Table Headers
  'table.fund': 'Fondas',
  'table.commitment': 'Įsipareigojimas',
  'table.called': 'Pareikalauta',
  'table.distributed': 'Paskirstyta',
  'table.tvpi': 'TVPI',
  'table.status': 'Būsena',
  'table.regulation': 'Reguliavimas',
  'table.filingType': 'Pateikimo tipas',
  'table.reportingPeriod': 'Laikotarpis',
  'table.createdAt': 'Sukurta',
  'table.actions': 'Veiksmai',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'ES Taksonomija',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Duomenų užbaigtumas',
  'compliance.nextDeadline': 'Kitas terminas',

  // Reports
  'report.quarterly': 'Ketvirtinė ataskaita',
  'report.annual': 'Metinė ataskaita',
  'report.paiStatement': 'PAI Pareiškimas',
  'report.taxonomyReport': 'Taksonomijos ataskaita',
  'report.generatedAt': 'Sugeneruota',
  'report.version': 'Versija',

  // General
  'general.loading': 'Kraunama...',
  'general.noData': 'Nėra prieinamų duomenų',
  'general.error': 'Įvyko klaida',
  'general.retry': 'Bandyti dar kartą',
  'general.total': 'Iš viso',
  'general.page': 'Puslapis',
  'general.of': 'iš',
  'general.showing': 'Rodoma',
  'general.results': 'rezultatai',
  'general.language': 'Kalba',
  'general.english': 'Anglų',
  'general.french': 'Prancūzų',

  // Tour
  'tour.takeTour': 'Pradeti apziura',
  'tour.skip': 'Praleisti',
  'tour.next': 'Kitas',
  'tour.prev': 'Ankstesnis',
  'tour.finish': 'Baigti',


  // Tour Content, Form Labels, Table Headers, Actions, Errors (i18n wiring)
  'tour.sidebarTitle': 'Navigacija',
  'tour.sidebarContent': 'Naudokite šoninę juostą naviguoti tarp portfelio valdymo, ESG duomenų rinkimo, atitikties, ataskaitų ir analitikos įrankių.',
  'tour.kpisTitle': 'Portfelio apžvalga',
  'tour.kpisContent': 'Šios kortelės rodo jūsų portfelį iš pirmo žvilgsnio — iš viso fondų, AUM, aktyvios investicijos ir investuojamų subjektų skaičius.',
  'tour.actionsTitle': 'Greiti veiksmai',
  'tour.actionsContent': 'Kurkite fondus, pridėkite investicijas, pakvieskite subjektus ir generuokite ataskaitas vienu paspaudimu.',
  'tour.languageTitle': 'Kalba',
  'tour.languageContent': 'Perjunkite tarp 24 oficialių ES kalbų. Visa sąsaja išverčiama akimirksniu.',
  'tour.aiTitle': 'AI Asistentas',
  'tour.aiContent': 'Užduokite klausimus apie savo portfelį bet kuriuo metu. AI asistentas turi pilną kontekstą apie jūsų fondus ir investicijas.',
  'action.createFund': 'Sukurti fondą',
  'action.editFund': 'Redaguoti fondą',
  'action.addInvestment': 'Pridėti investiciją',
  'action.addInvestee': 'Pridėti subjektą',
  'action.editInvestee': 'Redaguoti subjektą',
  'action.createTemplate': 'Sukurti šabloną',
  'action.createSubmission': 'Sukurti pateikimą',
  'action.createFiling': 'Sukurti pateikimą',
  'action.generateReport': 'Generuoti ataskaitą',
  'action.inviteUser': 'Pakviesti naudotoją',
  'action.sendInvitation': 'Siųsti pakvietimą',
  'action.saveChanges': 'Išsaugoti pakeitimus',
  'form.fundName': 'Fondo pavadinimas',
  'form.fundCode': 'Fondo kodas',
  'form.sfdrCategory': 'SFDR Kategorija',
  'form.baseCurrency': 'Bazinė valiuta',
  'form.targetSize': 'Tikslinis dydis',
  'form.vintageYear': 'Įsteigimo metai',
  'form.geographyFocus': 'Geografinis fokusas',
  'form.sectorFocus': 'Sektoriaus fokusas',
  'form.investmentCode': 'Investicijos kodas',
  'form.instrumentType': 'Instrumento tipas',
  'form.maturityDate': 'Išpirkimo data',
  'form.sector': 'Sektorius',
  'form.countryCode': 'Šalies kodas',
  'form.entityType': 'Subjekto tipas',
  'form.region': 'Regionas',
  'form.staffCount': 'Darbuotojų skaičius',
  'form.borrowers': 'Skolininkai',
  'form.contactName': 'Kontaktinis vardas',
  'form.contactEmail': 'Kontaktinis el. paštas',
  'form.templateName': 'Šablono pavadinimas',
  'form.templateType': 'Šablono tipas',
  'form.indicators': 'Rodikliai',
  'form.reportingPeriod': 'Ataskaitinis laikotarpis',
  'form.reportingYear': 'Ataskaitiniai metai',
  'form.regulation': 'Reguliavimas',
  'form.filingType': 'Pateikimo tipas',
  'form.reportType': 'Ataskaitos tipas',
  'form.emailAddress': 'El. pašto adresas',
  'form.role': 'Vaidmuo',
  'form.selectFund': 'Pasirinkite fondą...',
  'form.selectInvestee': 'Pasirinkite subjektą...',
  'table.code': 'Kodas',
  'table.investee': 'Subjektas',
  'table.instrument': 'Instrumentas',
  'table.amount': 'Suma',
  'table.currency': 'Valiuta',
  'table.maturity': 'Išpirkimas',
  'table.name': 'Pavadinimas',
  'table.country': 'Šalis',
  'table.type': 'Tipas',
  'table.period': 'Laikotarpis',
  'table.completion': 'Užbaigimas',
  'table.submitted': 'Pateikta',
  'table.version': 'Versija',
  'table.indicators': 'Rodikliai',
  'table.dueDate': 'Terminas',
  'table.generated': 'Sugeneruota',
  'table.email': 'El. paštas',
  'table.role': 'Vaidmuo',
  'error.createFailed': 'Nepavyko sukurti. Bandykite dar kartą.',
  'error.updateFailed': 'Nepavyko atnaujinti. Bandykite dar kartą.',
  'error.loadFailed': 'Nepavyko įkelti duomenų.',

  // Sidebar Sections
  'sections.portfolio': 'PORTFELIS',
  'sections.dataCollection': 'DUOMENŲ RINKIMAS',
  'sections.intelligence': 'ŽVALGYBA',
  'sections.ecosystem': 'EKOSISTEMA',
  'sections.admin': 'ADMINISTRAVIMAS',
  'sections.other': 'KITA',

  // Page Titles
  'pages.messagesTitle': 'Žinutės',
  'pages.taxonomyTitle': 'ES Taksonomija',
  'pages.biStudioTitle': 'BI Studio',
  'pages.knowledgeGraphTitle': 'Žinių grafas',
  'pages.benchmarksTitle': 'Lyginamieji rodikliai',
  'pages.mcpTitle': 'MCP integracija',
  'pages.tasksTitle': 'Užduotys',
  'pages.submitDataTitle': 'Pateikti ESG duomenis',

  // Empty States
  'empty.benchmarksComingSoon': 'Lyginamieji rodikliai netrukus',

  // Labels
  'labels.sfdr': 'SFDR',
  'labels.euTaxonomy': 'ES Taksonomija',
  'labels.csrd': 'CSRD',
  'labels.mifid': 'MiFID',
  'labels.other': 'Kita',
  'labels.critical': 'Kritinis',
  'labels.high': 'Aukštas',
  'labels.medium': 'Vidutinis',
  'labels.low': 'Žemas',
};
